import { isSuccess, useOpen, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalContactViewProvider } from "../../contactViews/local/LocalContactViewProvider";
import { archivedEventService } from "../../shared/api/event/archivedEventService";
import { eventService } from "../../shared/api/event/eventService";
import {
	ArchivedEventDetailResource,
	IContactIndexResource,
	IContactOptionResource,
	IEventContactIndexResource,
} from "../../shared/types";
import { AddContactDrawer } from "./eventContacts/AddContactDrawer";
import { EventContactList } from "./eventContacts/EventContactList";
import { StatusDrawer } from "./eventContacts/StatusDrawer";

interface Props {
	event: ArchivedEventDetailResource;
}

export const ArchivedEventContacts: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	const [statusDrawerContact, setStatusDrawerContact] = useState<IEventContactIndexResource | null>(null);
	const statusDrawer = useOpen();
	const queryClient = useQueryClient();
	const addDrawer = useOpen();
	const { showToast } = useToasts();

	const handleRemoveContact = useCallback(
		async (contact: IContactIndexResource) => {
			const response = await archivedEventService.removeContact(event.id, contact.id);
			if (isSuccess(response)) {
				showToast({
					type: "success",
					title: t("Remove contact"),
					body: t("Contact was successfully removed."),
				});
				void queryClient.invalidateQueries({ queryKey: archivedEventService.contactsKey(event.id) });
			}
		},
		[event, queryClient, showToast, t],
	);

	const handleAddContacts = useCallback(
		async (contacts: IContactOptionResource[]) => {
			const response = await archivedEventService.addContacts(event.id, { contactIds: contacts.map(({ id }) => id) });
			if (isSuccess(response)) {
				showToast({ type: "success", body: t("Contacts were successfully added."), title: t("Add contacts") });
				void queryClient.invalidateQueries({ queryKey: archivedEventService.contactsKey(event.id) });
				return true;
			}
			return false;
		},
		[event, showToast, t, queryClient],
	);

	const handleUpdateStatus = useCallback(
		async (contact: IContactIndexResource, status: string | null) => {
			const response = await archivedEventService.updateContactStatus(event.id, contact.id, { status });
			if (isSuccess(response)) {
				showToast({
					type: "success",
					body: t("Contact status was successfully saved."),
					title: t("Update contact status"),
				});
				void queryClient.invalidateQueries({ queryKey: eventService.indexKey });
				void queryClient.invalidateQueries({ queryKey: eventService.detailKey(event.id) });
				return true;
			}
			return false;
		},
		[event, showToast, t, queryClient],
	);

	return (
		<>
			<LocalContactViewProvider contactViewKey="eventContacts">
				<EventContactList
					eventId={event.id}
					eventContactService={archivedEventService}
					onRemove={handleRemoveContact}
					onAdd={addDrawer.handleOpen}
					onChangeStatus={(contact) => {
						setStatusDrawerContact(contact);
						statusDrawer.handleOpen();
					}}
				/>
			</LocalContactViewProvider>
			<AddContactDrawer open={addDrawer.open} onClose={addDrawer.handleClose} onSubmit={handleAddContacts} />
			{statusDrawerContact && (
				<StatusDrawer
					open={statusDrawer.open}
					contact={statusDrawerContact}
					onClose={statusDrawer.handleClose}
					onClosed={() => setStatusDrawerContact(null)}
					onSubmit={(status) => handleUpdateStatus(statusDrawerContact, status)}
				/>
			)}
		</>
	);
};
